import { findChildrenInRange, mergeAttributes, Node, nodeInputRule, Tracker } from "@tiptap/core"
import { normalizeFirstPartyImageSrc } from "@/tiptap/core/editor-clipboard"
import { inferImageAlign, isImageCaptionFigure } from "@/tiptap/nodes/image-node/image-node-extension"

export const inputRegex = /!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\)/

function imageLikeAttrs(node) {
  return {
    src: node.attrs.src,
    alt: node.attrs.alt,
    title: node.attrs.title,
    width: node.attrs.width,
    height: node.attrs.height,
    style: node.attrs.style,
    "data-align": node.attrs["data-align"],
    "data-mce-style": node.attrs["data-mce-style"],
  }
}

export const Figure = Node.create({
  name: "figure",

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  group: "block",

  content: "inline*",

  draggable: true,

  isolating: true,

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) =>
          normalizeFirstPartyImageSrc(element.querySelector("img")?.getAttribute("src")),
      },

      alt: {
        default: null,
        parseHTML: (element) => element.querySelector("img")?.getAttribute("alt"),
      },

      title: {
        default: null,
        parseHTML: (element) => element.querySelector("img")?.getAttribute("title"),
      },

      width: {
        default: null,
        parseHTML: (element) => element.querySelector("img")?.getAttribute("width"),
      },

      height: {
        default: null,
        parseHTML: (element) => element.querySelector("img")?.getAttribute("height"),
      },

      style: {
        default: null,
        parseHTML: (element) =>
          element.querySelector("img")?.getAttribute("style") ||
          element.querySelector("img")?.getAttribute("data-mce-style"),
      },

      "data-mce-style": {
        default: null,
        parseHTML: (element) => element.querySelector("img")?.getAttribute("data-mce-style"),
      },

      "data-align": {
        default: null,
        parseHTML: (element) =>
          element.getAttribute("data-align") ||
          inferImageAlign(element.querySelector("img")),
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: "figure",
        getAttrs: (element) => {
          if (!element.querySelector?.("img")) return false
          if (isImageCaptionFigure(element)) return false
          return {}
        },
        contentElement: (element) =>
          element.querySelector("figcaption") || element.ownerDocument.createElement("figcaption"),
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const {
      src,
      alt,
      title,
      width,
      height,
      style,
      "data-align": align,
      "data-mce-style": mceStyle,
      ...rest
    } = HTMLAttributes

    const figureAttrs = mergeAttributes(this.options.HTMLAttributes, rest)
    if (align) figureAttrs["data-align"] = align

    const imgAttrs = { src, draggable: false, contenteditable: false }
    if (alt) imgAttrs.alt = alt
    if (title) imgAttrs.title = title
    if (width) imgAttrs.width = width
    if (height) imgAttrs.height = height
    if (style) imgAttrs.style = style
    if (mceStyle) imgAttrs["data-mce-style"] = mceStyle

    return ["figure", figureAttrs, ["img", imgAttrs], ["figcaption", 0]]
  },

  addCommands() {
    return {
      setFigure:
        ({ caption, ...attrs }) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs,
              content: caption ? [{ type: "text", text: caption }] : [],
            })
            .command(({ tr, commands }) => {
              const { doc, selection } = tr
              const position = doc.resolve(selection.to - 2).end()

              return commands.setTextSelection(position)
            })
            .run()
        },

      imageToFigure:
        () =>
        ({ tr, commands }) => {
          const { doc, selection } = tr
          const { from, to } = selection
          const images = findChildrenInRange(doc, { from, to }, (node) => node.type.name === "image")

          if (!images.length) {
            return false
          }

          const tracker = new Tracker(tr)

          return images.every(({ node, pos }) => {
            const mapResult = tracker.map(pos)

            if (mapResult.deleted) {
              return false
            }

            const range = {
              from: mapResult.position,
              to: mapResult.position + node.nodeSize,
            }

            return commands.insertContentAt(range, {
              type: this.name,
              attrs: imageLikeAttrs(node),
            })
          })
        },

      figureToImage:
        () =>
        ({ tr, commands }) => {
          const { doc, selection } = tr
          const { from, to } = selection
          const figures = findChildrenInRange(
            doc,
            { from, to },
            (node) => node.type.name === this.name,
          )

          if (!figures.length) {
            return false
          }

          const tracker = new Tracker(tr)

          return figures.every(({ node, pos }) => {
            const mapResult = tracker.map(pos)

            if (mapResult.deleted) {
              return false
            }

            const range = {
              from: mapResult.position,
              to: mapResult.position + node.nodeSize,
            }

            return commands.insertContentAt(range, {
              type: "image",
              attrs: imageLikeAttrs(node),
            })
          })
        },
    }
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: (match) => {
          const [, alt, src, title] = match

          return { src, alt, title }
        },
      }),
    ]
  },
})

export default Figure
